/* ============================================================
   Mighty slide-out cart — one-time → subscription upgrade (Phase 2)
   Native Shopify selling-plan swap via /cart/change.js. One-way (no
   frontend downgrade). Converts every one-time line to the plan.
   Isolated module; hooks the existing globals (routes, fetchConfig,
   publish, PUB_SUB_EVENTS) and re-renders from server truth.
   ============================================================ */
(function () {
  'use strict';

  async function refreshDrawer() {
    // Re-render the whole drawer inner + cart bubble from a fresh section render.
    const [drawerText, bubbleText] = await Promise.all([
      fetch(`${routes.cart_url}?section_id=cart-drawer`).then((r) => r.text()),
      fetch(`${routes.cart_url}?section_id=cart-icon-bubble`).then((r) => r.text()).catch(() => null),
    ]);
    const html = new DOMParser().parseFromString(drawerText, 'text/html');
    const src = html.querySelector('.drawer__inner');
    const tgt = document.querySelector('#CartDrawer .drawer__inner');
    if (src && tgt) tgt.innerHTML = src.innerHTML;
    const srcHost = html.querySelector('cart-drawer');
    const liveHost = document.querySelector('cart-drawer');
    if (srcHost && liveHost) liveHost.classList.toggle('is-empty', srcHost.classList.contains('is-empty'));
    if (bubbleText) {
      const bd = new DOMParser().parseFromString(bubbleText, 'text/html');
      const nb = bd.getElementById('cart-icon-bubble');
      const cb = document.getElementById('cart-icon-bubble');
      if (nb && cb) cb.innerHTML = nb.innerHTML;
    }
  }

  function giftVariantIds() {
    // Gifts are identified by variant id (source of truth: the offers script tag's
    // data-gift-variants). The _gift line property is no longer reliable — gifts are added
    // via /cart/update.js (which can't set properties) to avoid Shopify's web pixel firing
    // Meta AddToCart per gift.
    const s = document.querySelector('script[src*="mighty-cart-offers"]');
    return (s && s.dataset.giftVariants ? s.dataset.giftVariants : '')
      .split(',').map((v) => v.trim()).filter(Boolean);
  }

  async function upgradeToSubscription(planId) {
    // Convert each one-time line to the subscription plan. Re-fetch between
    // conversions because a line's key changes once a selling plan is attached.
    const gifts = giftVariantIds();
    for (let guard = 0; guard < 20; guard++) {
      const cart = await (await fetch(`${routes.cart_url}.js`)).json();
      // Only convert real one-time PRODUCT lines — never the free gifts. Gifts have no
      // selling plan either, and change.js on a gift variant returns 422 "Cannot apply
      // selling plan to variant", which used to abort the whole upgrade mid-flight.
      const oneTime = cart.items.find(
        (it) => !it.selling_plan_allocation
          && gifts.indexOf(String(it.variant_id)) === -1
          && (!it.properties || it.properties._gift !== 'true')
      );
      if (!oneTime) break;
      const body = JSON.stringify({
        id: oneTime.key,
        quantity: oneTime.quantity,
        selling_plan: Number(planId),
      });
      const res = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body });
      if (!res.ok) throw new Error(`change.js ${res.status}`);
    }
  }

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.mc-subscribe-btn');
    if (!btn) return;
    e.preventDefault();
    const planId = btn.dataset.sellingPlan;
    if (!planId || btn.classList.contains('is-loading')) return;

    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    try {
      await upgradeToSubscription(planId);
      // Persist an upgrade flag as a cart attribute so the success banner
      // survives every server re-render (not just this render).
      await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({ attributes: { _mc_upgraded: 'true' } }),
      }).catch(() => {});
      // Apply the discount codes BEFORE the drawer repaints so the first render shows the
      // discounted price ($39), not the raw subscription price ($67). settle() runs
      // applyCodes + a single refreshDrawer; clearUpgrade=false keeps the "subscribed &
      // saving" banner we just set. Fall back to a plain re-render if offers.js is absent.
      if (typeof window.mightyCartSettle === 'function') {
        await window.mightyCartSettle(false, false);
      } else {
        await refreshDrawer();
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mighty-subscribe' });
      }
      // On success the panel is gone (no one-time lines) — the button no longer exists.
    } catch (err) {
      console.error('[mighty-cart] subscription upgrade failed', err);
      // ALWAYS re-render from server truth after a failure. A partial upgrade may have
      // already converted the one-time line on the server; without this the drawer keeps
      // showing the stale one-time item that no longer exists as such — the shopper then
      // checks out and finds it missing/changed. Render first, THEN write the error (the
      // re-render replaces the drawer HTML, including the error box).
      try { await refreshDrawer(); } catch (e) {}
      const errBox = document.getElementById('CartDrawer-CartErrors');
      if (errBox) errBox.textContent = 'Could not upgrade to subscription. Please try again.';
    }
  });
})();
